import { useEffect, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { Delete, Loader2, Lock } from "lucide-react";

import { EXIT_KID_MODE } from "@/graphql/mutations/kid-mode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth/AuthContext";

const MAX_LENGTH = 8;
const MIN_LENGTH = 4;

/**
 * The way out of the kid shell. Deliberately dull: a small lock in the corner,
 * no hints about what the PIN might be. The lockout after repeated misses is
 * enforced on the server — the countdown here only explains the wait.
 *
 * The PIN field is a real focused input, so a grown-up at a keyboard just types
 * and presses Enter. The number pad is for the case it was built for — a tablet
 * with no keyboard — and stays out of the way otherwise.
 */
export function ParentExitDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { ownerName } = useAuth();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [exitKidMode, { loading }] = useMutation(EXIT_KID_MODE);
  const inputRef = useRef<HTMLInputElement>(null);

  // A touch device may have no keyboard at all, so that is the one case where the
  // pad earns its space. Checked once — nobody grows a keyboard mid-dialog.
  const [padVisible] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches
  );

  // Reset on every open so a previous attempt is never left on screen.
  useEffect(() => {
    if (open) {
      setPin("");
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setInterval(() => setWaitSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [waitSeconds]);

  const locked = waitSeconds > 0;

  const submit = async (value: string) => {
    if (locked || loading) return;

    try {
      const { data } = await exitKidMode({ variables: { pin: value } });
      const result = (data as any)?.exitKidMode;

      if (result?.success) {
        // The auth cookie was replaced server-side; a full reload is the only
        // way to be sure no learner-scoped cache survives into the parent view.
        window.location.assign("/");
        return;
      }

      setPin("");
      setError(result?.error ?? "Nepavyko");
      if (result?.retryAfterSeconds) setWaitSeconds(result.retryAfterSeconds);
    } catch {
      setPin("");
      setError("Nepavyko. Pabandyk vėliau.");
    }
  };

  const press = (digit: string) => {
    if (locked || loading) return;
    setError(null);

    const next = (pin + digit).slice(0, MAX_LENGTH);
    setPin(next);
    inputRef.current?.focus();
  };

  // Digits only, so a typed letter or a pasted phone number cannot end up in a
  // PIN the server will only ever reject.
  const typed = (value: string) => {
    setError(null);
    setPin(value.replace(/\D/g, "").slice(0, MAX_LENGTH));
  };

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
  const ready = !locked && !loading && pin.length >= MIN_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Tėvams
          </DialogTitle>
          <DialogDescription>
            {ownerName
              ? `Įvesk PIN, kad grįžtum į ${ownerName} paskyrą.`
              : "Įvesk tėvų PIN kodą."}
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) submit(pin);
          }}
        >
          {/* Masked, so a PIN typed over a child's shoulder stays private. */}
          <Input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="current-password"
            autoFocus
            placeholder="••••"
            aria-label="PIN kodas"
            className="h-12 text-center text-2xl tracking-[0.5em]"
            value={pin}
            disabled={locked || loading}
            onChange={(e) => typed(e.target.value)}
          />

          {error && (
            <p className="text-center text-sm text-destructive" role="alert">
              {error}
              {locked && ` (${waitSeconds} s)`}
            </p>
          )}

          <div className="flex gap-2">
            <Button type="submit" className="flex-1" disabled={!ready}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerai"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={locked || loading || pin.length === 0}
              onClick={() => {
                setPin("");
                inputRef.current?.focus();
              }}
              aria-label="Išvalyti"
            >
              <Delete className="h-4 w-4" />
            </Button>
          </div>

          {padVisible && (
            <div className="grid grid-cols-3 gap-2 pt-2">
              {keys.map((key) => (
                <Button
                  key={key}
                  type="button"
                  variant="outline"
                  className="h-14 text-xl font-semibold"
                  disabled={locked || loading}
                  onClick={() => press(key)}
                >
                  {key}
                </Button>
              ))}
              <Button
                type="button"
                variant="outline"
                className="col-span-3 h-14 text-xl font-semibold"
                disabled={locked || loading}
                onClick={() => press("0")}
              >
                0
              </Button>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
